<template>
  <div
    class="asset-library"
    :class="[
      { 'asset-library--relative' : isInline },
      { 'asset-library--inactive' : isUploading }
    ]"
  >
    <div v-if="isUploading" class="asset-library__loading-icon">
      <fa :icon="['fad', 'spinner-third']" />
    </div>
    <div class="asset-library__modal">
      <button v-if="!isInline" class="asset-library__close-btn btn btn-sm btn-dark" @click="$emit('close')"><fa :icon="['fas', 'times']" size="lg" /></button>
      <button class="btn btn-primary" @click="showUpload">{{ isUploadVisible ? 'Close' : 'Upload Asset' }}</button>

      <div v-show="isUploadVisible" class="asset-library__upload mt-2">
        <div class="row">
          <div class="col-4">
            <label for="assetDescription">Choose Asset File</label>
            <input type="file" name="asset" ref="assetInput" id="asset" @change="isUploadFileChosen = !!$refs.assetInput.files.length" multiple>
          </div>
          <div class="col-4">
            <label for="assetDescription">Asset Description</label>
            <input class="form-control w-100" id="assetDescription" type="text" v-model="assetDescription" placeholder="i.e. masterplan">
          </div>
          <div class="col-4">
            <label>&nbsp;</label>
            <button class="float-right btn btn-sm btn-secondary" :disabled="!isUploadFileChosen" @click="createAsset($refs.assetInput.files)">Upload Asset</button>
          </div>
        </div>
      </div>

      <div class="row my-4">
        <div class="col-4">
          <div class="asset-library__categories">
            <label for="">Category</label>
            <select v-model="selectedCategory" class="form-control">
              <option value="">All</option>
              <option v-for="category in assetCategories" :key="category" :value="category">{{ transformToStartCase(category) }}</option>
            </select>
          </div>
        </div>
        <div class="col-4">
          <label for="">File Type</label>
          <select v-model="selectedType" class="form-control">
            <option value="">All</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
        </div>
        <div class="col-4">
          <label for="">Filter</label>
          <input class="form-control w-100" type="text" v-model="filterString">
        </div>
      </div>

      <table
        class="asset-table"
        :class="{ 'asset-table--asset-hover' : isDroppingAsset && dropAssetCounter > 0 }"
        @dragenter.prevent.stop="uploadDropEnter"
        @dragleave.prevent.stop="uploadDropLeave"
        @dragover.prevent.stop="uploadDropOver"
        @drop.prevent.stop="uploadDroppedAssets"
      >
        <thead>
          <tr>
            <th style="width: 45px;"></th>
            <th @click="sortBy('description')">Title <fa v-if="sortedType === 'description'" class="arrow" :class="{ 'fa-rotate-180': !isSortReversed }" :icon="['fa', 'chevron-down']" /></th>
            <th @click="sortBy('path')" class="asset-paths">Thumbnail Path <fa v-if="sortedType === 'path'" class="arrow" :class="{ 'fa-rotate-180': !isSortReversed }" :icon="['fa', 'chevron-down']" /></th>
            <th @click="sortBy('dimensions')" class="asset-dimensions">Dimensions <fa v-if="sortedType === 'dimensions'" class="arrow" :class="{ 'fa-rotate-180': !isSortReversed }" :icon="['fa', 'chevron-down']" /></th>
            <th @click="sortBy('filesize')" class="asset-filesize">Filesize <fa v-if="sortedType === 'filesize'" class="arrow" :class="{ 'fa-rotate-180': !isSortReversed }" :icon="['fa', 'chevron-down']" /></th>
            <th @click="sortBy('dateUploaded')" class="asset-date">Date uploaded <fa v-if="sortedType === 'dateUploaded'" class="arrow" :class="{ 'fa-rotate-180': !isSortReversed }" :icon="['fa', 'chevron-down']" /></th>
            <th class="asset-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="asset in filteredAssets"
            :key="asset._id"
            class="asset"
            :class="[
              { 'asset--active': isActive(asset) },
              { 'asset--edit': isEditingAsset === asset }
            ]"
            ref="assetsWrapper"
            @click="selectAsset(asset)"
            @dblclick="quickEdit(asset)"
          >
            <td class="asset-checkbox">
              <div class="asset-checkbox__checkbox" :class="{ 'asset-checkbox__checkbox--checked' : isActive(asset) }">
                <fa :icon="['fas', 'check']" />
              </div>
            </td>
            <td>
              <div class="asset-thumbnail">
                <img 
                  class="asset-thumbnail__image"
                  :src="asset.customThumbPath ? $store.state.assetsPath + asset.customThumbPath : $store.state.assetsPath + asset.thumbPath"
                  draggable="false"
                >
                <div class="asset-thumbnail__description">
                  {{ !isActive(asset) || !(isEditingAsset === asset) ? asset.description : null }}
                   <input
                    v-if="isActive(asset) && isEditingAsset === asset"
                    type="text"
                    v-model="asset.description"
                    @click.stop=""
                  />
                </div>
              </div>
            </td>
            <td class="asset-paths">
              <div>
                <div class="asset-paths__thumb-path">
                  <input
                    type="text"
                    v-model="asset.thumbPath"
                    disabled="true"
                    placeholder="Thumbnail"
                    @click.stop=""
                  />
                </div>
                <div
                  v-if="asset.type == 'video'"
                  class="asset-paths__custom-thumb-path"
                >
                  <input
                    type="text"
                    v-model="asset.customThumbPath"
                    :disabled="!isActive(asset) || !(isEditingAsset === asset)"
                    placeholder="Custom thumbnail"
                    @click.stop=""
                  />
                </div>
              </div>
            </td>
            <td class="asset-dimensions">
              <div>
                {{ asset.dimensions }}
              </div>
            </td>
            <td class="asset-filesize">
              <div>
                {{ bytesToSize(asset.filesize) }}
              </div>
            </td>
            <td class="asset-date">
              <div>
                {{ convertDate(asset.createdAt) }}
              </div>
            </td>

            <td class="asset-actions">
              <div class="asset-actions__wrapper">
                <button 
                  v-if="isActive(asset) && !(isEditingAsset === asset)"
                  class="btn btn-link asset-actions__button asset-actions__button--trash"
                  @click.stop="deleteAsset(asset)"
                >
                  <fa :icon="['fas', 'trash']" size="lg" />
                </button>
                <button 
                  v-if="isActive(asset) && !(isEditingAsset === asset)"
                  class="btn btn-link asset-actions__button"
                  @click.stop="isEditingAsset = asset"
                >
                  <fa :icon="['fas', 'pen-square']" size="lg" />
                </button>
                <button
                    class="btn btn-link"
                    v-if="isActive(asset) && isEditingAsset === asset"
                    @click.stop="updateAsset(asset)"
                  >
                    <fa :icon="['fas', 'save']" size="lg" />
                  </button>
                <a
                  class="btn btn-link asset-actions__button asset-actions__button--view"
                  :href="$store.state.assetsPath + asset.path"
                  target="_blank"
                  @click.stop=""
                >
                  <fa :icon="['fas', 'eye']" size="lg" />
                </a>
              </div>
              
            </td>
          </tr>
        </tbody>

        <div class="asset-table__delete-btn-wrapper">
          <button
            v-if="isInline"
            class="asset-table__delete-btn btn btn-danger"
            :class="{ 'asset-table__delete-btn--active' : selectedAssets.length > 1 }"
            @click="deleteAsset()"
          >
            <fa :icon="['fas', 'trash']" size="lg" /> {{ selectedAssets.length > 1 ? selectedAssets.length : '2' }} Assets
          </button>
        </div>
      </table>

      <button
        v-if="!isInline"
        :disabled="multiple ? ! selectedAssets.length : !selectedAsset"
        class="btn btn-primary mt-4 float-right"
        @click="useAsset()"
      >
        <fa :icon="['fas', 'paperclip']" size="lg" /> {{ multiple ? selectedAssets.length : '' }} Asset{{ multiple ? 's' : '' }}
      </button>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import { ASSET_DELETE } from '@/store/actions.type'
import Swal from 'sweetalert2'

export default {
  props: ['multiple', 'value', 'isInline', 'defaultCategory', 'defaultType'],

  data () {
    return {
      assetDescription: '',
      filterString: '',
      dropAssetCounter: 0,
      isDroppingAsset: false,
      isEditing: false,
      isEditingAsset: '',
      isUploadFileChosen: false,
      isUploading: false,
      isUploadVisible: false,
      selectedAsset: null,
      selectedAssets: [],
      selectedCategory: this.defaultCategory ? this.defaultCategory : '',
      selectedType: this.defaultType ? this.defaultType : '',
      isSortReversed: false,
      sortedType: 'description'
    }
  },

  computed: {
    assetCategories () {
      return _.orderBy(this.$store.state.assetLibrary.assetCategories)
    },

    assets () {
      if (this.isSortReversed) {
        return _.sortBy(this.$store.state.assetLibrary.assets, asset => {
          return asset[this.sortedType]
        }).reverse()
      } else {
        return _.sortBy(this.$store.state.assetLibrary.assets, asset => {
          return asset[this.sortedType]
        })
      }
    },

    filteredAssets () {
      return _.filter(this.assets, asset => {
        if (this.selectedCategory === ''|| this.selectedCategory === asset.category) {
          if (this.selectedType === '') {
            return asset.description.toLowerCase().includes(this.filterString.toLowerCase()) || asset.path.toLowerCase().includes(this.filterString.toLowerCase())
          } else if (this.selectedType === asset.type) {
            return asset.description.toLowerCase().includes(this.filterString.toLowerCase()) || asset.path.toLowerCase().includes(this.filterString.toLowerCase())
          }
        }
      })
     
    },
  },

  mounted () {
    if (this.multiple) {
      this.selectedAssets = this.value
    } else {
      if (this.value !== null && this.$refs.selectedAsset !== undefined) {
        this.selectedAsset = this.value
        this.$nextTick(() => {
          this.$refs.assetsWrapper[0].scrollIntoView()
        })
      }
    }
  },

  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateValue();
      }
    }
  },

  methods: {
    bytesToSize(bytes) {
      if (bytes === null || bytes === undefined) {
        return 'n/a'
      }

      if (bytes === 0) {
        return '0 Byte'
      }

      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

      let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)))
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100  + ' ' + sizes[i]
    },

    convertDate (date) {
      let formattedDate = new Date(date)

      return moment(new Date(date)).format('DD MMM YYYY')
    },

    createAsset (files) {
      this.isUploading = true
      if (files.length > 1) {
        for (var i = 0; i < files.length; i++) {
          const asset = this.$store.dispatch('UPLOAD_ASSET', {
            category: this.selectedCategory ? this.selectedCategory : 'misc',
            description: files[i].name,
            file: files[i]
          }).then(() => {
            this.isUploading = false
          })
        }
      } else {
        const asset = this.$store.dispatch('UPLOAD_ASSET', {
          category: this.selectedCategory ? this.selectedCategory : 'misc',
          description: this.assetDescription ? this.assetDescription : files[0].name,
          file: files[0]
        }).then(() => {
          this.isUploading = false
        })
      }

      this.$refs.assetInput.value = ''
    },

    deleteAsset (asset) {
      const confirmText = asset ? 'Delete asset' : `Delete ${this.selectedAssets.length} assets`
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: confirmText
      }).then((result) => {
        if (result.value) {
          Swal.fire(
            'Deleted!',
            'The asset has been deleted.',
            'success'
          )

          if (asset) {
            this.$store
              .dispatch('deleteAsset', asset)
              .then(res => {
                if (res.data.errors) {
                  throw res.data.errors 
                }
                this.$notify({
                  message: `${asset.description} has been deleted`,
                  horizontalAlign: "center",
                  verticalAlign: "top",
                  type: "success"
                });
                this.selectedAssets = []
                this.$emit('updateAssets')
              })
              .catch(err => {
                if (typeof err === 'object') {
                  err.forEach(error => {
                    this.$notify({
                      message: error,
                      horizontalAlign: "center",
                      verticalAlign: "top",
                      type: "warning"
                    });
                  })
                } else {
                  this.$notify({
                    message: err.message,
                    horizontalAlign: "center",
                    verticalAlign: "top",
                    type: "warning"
                  });
                }     
              })
          } else {
            this.selectedAssets.forEach(asset => {
              this.$store
                .dispatch('deleteAsset', asset)
                .then(res => {
                  if (res.data.errors) {
                    throw res.data.errors 
                  }
                  this.$notify({
                    message: `${asset.description} has been deleted`,
                    horizontalAlign: "center",
                    verticalAlign: "top",
                    type: "success"
                  });
                  this.selectedAssets = []
                  this.$emit('updateAssets')
                })
                .catch(err => {
                  if (typeof err === 'object') {
                    err.forEach(error => {
                      this.$notify({
                        message: error,
                        horizontalAlign: "center",
                        verticalAlign: "top",
                        type: "warning"
                      });
                    })
                  } else {
                    this.$notify({
                      message: err.message,
                      horizontalAlign: "center",
                      verticalAlign: "top",
                      type: "warning"
                    });
                  }     
                })
            })
          }
        }
      })
    },

    isActive (asset) {
      if (this.multiple) {
        let active = false
        this.selectedAssets.forEach(selectedAsset => {
          if (selectedAsset.path === asset.path) {
            active = true
          }
        })
        return active
      } else {
        return this.selectedAsset ? this.selectedAsset.path === asset.path : false
      }
    },

    selectAsset (asset) {
      this.isEditingAsset = null
      if (this.multiple) {
        if (this.isActive(asset)) {
          _.remove(this.selectedAssets, selectedAsset => {
            return selectedAsset.path === asset.path
          })
          this.selectedAssets = JSON.parse(JSON.stringify(this.selectedAssets))
        } else {
          this.selectedAssets.push(asset)
        }
      } else {
        this.selectedAsset = asset
      }
    },

    quickEdit (asset) {

        this.selectedAssets = []
        this.selectedAssets.push(asset)
        this.isEditingAsset = asset
 
    },

    setSelectedCategory (category) {
      this.selectedCategory = category
    },

    showUpload () {
      this.isUploadVisible = !this.isUploadVisible
    },

    sortBy(type) {
      this.isSortReversed = !this.isSortReversed
      this.sortedType = type
    },

    transformToStartCase (string) {
      return _.startCase(_.camelCase(string))
    },

    updateAsset (asset) {
      this.isEditingAsset = null
      this.$store.dispatch('UPDATE_ASSET', asset)
      this.$refs.assetsWrapper.scrollTop = 0
    },

    useAsset () {
      if (this.multiple) {
        this.$emit('input', this.selectedAssets)
        this.$emit('close')
      } else {
        this.$emit('input', this.selectedAsset)
        this.$emit('close')
      }
    },

    validateValue () {
      if (this.multiple) {
        return Array.isArray(this.value)
      } else {
        return typeof this.value === 'string' || this.value === null
      }
    },

    uploadDropEnter (e) {
      console.log('drop enter')
      e.stopPropagation()
      e.preventDefault()
      this.dropAssetCounter++
    },

    uploadDropOver (e) {
      let isFile = true

      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i].type.substring(0, 5) !== 'image' && e.dataTransfer.items[i].type.substring(0, 5) !== 'video') {
          isFile = false
        }
      }

      if (isFile) {
        this.isDroppingAsset = true
      }
    },

    uploadDropLeave () {
      this.dropAssetCounter--
    },

    uploadDroppedAssets (e) {
      let isFile = true
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        // TODO restrict allowed image / video types
        if (e.dataTransfer.items[i].type.substring(0, 5) !== 'image' && e.dataTransfer.items[i].type.substring(0, 5) !== 'video') {
          isFile = false
        }
      }

      if (isFile) {
        this.isDroppingAsset = false
        this.createAsset(e.dataTransfer.files)
      }
    }
  }
}
</script>
<style lang="scss">
@import '@/styles/_global.scss';
@import '@/styles/_animations.scss';
@import '@/styles/_mixins.scss';

.asset-table {
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  width: 100%;

  tbody {
    display: block;
    max-height: none;
    overflow: auto;
    scroll-behavior: smooth;

    @include v-breakpoint(800px) {
      max-height: calc(100vh - 500px);
    }
  }

  thead, tbody tr {
    display: table;
    table-layout: fixed;
    width: 100%;
  }

  thead {
    background-color: #f0f0f0;
    border-bottom: 1px solid #d4d4d4;
    font-size: 12px;
    text-transform: uppercase;
    top: 0;
    z-index: 10;
  }

  th {
    cursor: pointer;

    &:hover {
      background-color: darken(#f0f0f0, 10%);
    }
  }

  th, td {
    font-weight: 400;
    padding: 10px 15px;
  }

  tr {
    margin: 4px 0;
  }

  &::before {
    background: rgba(0, 0, 0, 0);
    bottom: 0;
    content: '';
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    transition: background 300ms;
    z-index: 100;
  }

  &::after {
    color: #ffffff;
    content: 'Upload Asset';
    font-size: 40px;
    left: 50%;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -20%);
    transition: 300ms all 150ms;
    z-index: 110;
  }

  &--asset-hover {
    &::before {
      background: rgba(0, 0, 0, 0.2);
      pointer-events: auto;
    }

    &::after {
      opacity: 1;
      pointer-events: auto;
      transform: translate(-50%, -50%);
    }
  }

  &__delete-btn-wrapper {
    bottom: 20px;
    height: 38px;
    left: 20px;
    pointer-events: none;
    position: absolute;
    width: 200px;
  }

  &__delete-btn {
    opacity: 0 !important;
    position: absolute;
    transform: translateY(100%);

    &--active {
      opacity: 1 !important;
      pointer-events: auto;
      transform: translateY(0);
    }
  }

  .arrow {
    transition: transform 200ms;
  }
}

.asset {
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
    
    .asset-actions__wrapper {
      display: flex;
    }
  }

  &--active {
    background: #e0e0e0;

    &:hover {
      background: #e0e0e0;
    }
  }

  &--edit {
    background: #a1ceff;

    &:hover {
      background: #a1ceff;
    }
  }
}

.asset-checkbox {
  background: #fff;
  padding: 10px 15px 10px 15px !important;
  width: 45px;

  &__checkbox {
    border: 1px solid grey;
    border-radius: 2px;
    height: 15px;
    width: 15px;

    svg {
      color: #007bff;
      opacity: 0 !important;
      transform: scale(.5) translateY(-4px);
      transition: all 200ms;
    }

    &--checked {
      svg {
        opacity: 1 !important;
        transform: scale(1) translateY(-4px);
      }
    }
  }
}

.asset-thumbnail {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;

  &__image {
    border-radius: 50%;
    display: flex;
    flex-shrink: 0;
    height: 70px;
    object-fit: cover;
    overflow: hidden;
    pointer-events: none;
    user-select: none;
    width: 70px;
  }

  &__description {
    margin: 0 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    input {
      width: 100%;
    }
  }
}

.asset-label {
  font-size: 9px !important;
  font-weight: 700 !important;
  letter-spacing: 0;
  margin: .5em 0 .25em 0 !important;
}

.asset-paths {
  input {
    width: 100%;
  }
}

.asset-dimensions {
  width: 150px;
}

.asset-filesize {
  width: 150px;
}

.asset-date {
  width: 150px;
}

.asset-actions {
  width: 200px;

  &__wrapper {
    align-items: center;
    display: none;
  }

  &__button {
    border-radius: 50%;

    &--trash {
      color: #e72c2c !important;

      &:hover {
        color: darken(#e72c2c, 10%) !important;
      }
    }

    &--view {
      margin: 0 0 0 auto;
    }
  }
}

.asset-library {
  align-items: center;
  background: rgba(#000000, .5);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 999;

  &--inactive {
    pointer-events: none;
    user-select: none;

    .asset-library__modal * {
      opacity: .8;
    }
  }

  &--relative {
    position: relative;

    .asset-library__modal {
      border-radius: 0;
      padding: 0;
      width: 100%;
    }
  }

  &__loading-icon {
    color: green;
    font-size: 80px;
    left: 50%;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    user-select: none;
    z-index: 10;

    svg {
      animation-name: rotate;
      animation-duration: 3s;
      animation-iteration-count: infinite;
      animation-timing-function: linear;
    }
  }

  &__heading {
    color: #252422;
    font-weight: 300;
    margin-top: 0;
  }

  &__modal {
    border-radius: 4px;
    background-color: #fff;
    padding: 2em;
    position: relative;
    width: 80vw;
  }

  &__close-btn {
    position: absolute;
    right: 20px;
  }

  &__upload {
    border: 1px solid #d4d4d4;
    border-radius: 4px;
    padding: 15px;
  }
}
</style>
